import React, { useCallback, useEffect, useState } from 'react';
import GameDetailModal from '../../components/GameDetailModal/GameDetailModal';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import api from '../../api';
import { GAME_DETAIL_PREVIEW_SCENARIOS, MOCK_MATCHUP_STATS, MOCK_LINE_HISTORY, MOCK_LINE_HISTORY_LIVE } from '../../dev/mockGameDetailGames';
import {
  REAL_2025_PREVIEW_GAMES,
  REAL_2025_SEASON,
  REAL_2025_WEEK,
  asScheduledPreview,
} from '../../dev/real2025PreviewGames';
import styles from './GameDetailPreviewPage.module.css';

/**
 * Dev-only page to exercise GameDetailModal across scheduled / live / final states.
 * Visit /dev/game-detail-preview while logged in (development build only).
 */
const GameDetailPreviewPage = () => {
  const { currentWeek } = useCurrentWeek();
  const displayedWeek = currentWeek?.week ?? 12;

  const [activeScenario, setActiveScenario] = useState(null);
  const [real2025Games, setReal2025Games] = useState([]);
  const [real2025Loading, setReal2025Loading] = useState(true);
  const [real2025Error, setReal2025Error] = useState(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;

    let cancelled = false;
    setReal2025Loading(true);
    setReal2025Error(null);

    api.fetchGames(REAL_2025_WEEK, REAL_2025_SEASON)
      .then((response) => {
        if (cancelled) return;
        setReal2025Games(response.data?.games || []);
      })
      .catch(() => {
        if (!cancelled) {
          setReal2025Error('Could not load 2025 Week 8 games. Is the backend running on :3001?');
        }
      })
      .finally(() => {
        if (!cancelled) setReal2025Loading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpen = useCallback((scenario) => {
    setActiveScenario(scenario);
  }, []);

  const handleClose = useCallback(() => {
    setActiveScenario(null);
  }, []);

  const handlePickAction = useCallback((action) => {
    // eslint-disable-next-line no-console
    console.log('[GameDetailPreview] pick action (no-op):', action);
    handleClose();
  }, [handleClose]);

  const openReal2025Scenario = useCallback((config) => {
    const game = real2025Games.find((g) => g.id === config.gameId);
    if (!game) return;

    const previewGame = config.mode === 'scheduled' ? asScheduledPreview(game) : game;

    setActiveScenario({
      id: config.id,
      label: config.label,
      game: previewGame,
      initialSelectedPick: null,
      previewLocks: null,
      previewMode: false,
      displayedWeek: REAL_2025_WEEK,
    });
  }, [real2025Games]);

  if (process.env.NODE_ENV !== 'development') {
    return (
      <div className={styles.page}>
        <p>Game detail preview is only available in development builds.</p>
      </div>
    );
  }

  const modalDisplayedWeek = activeScenario?.displayedWeek ?? displayedWeek;
  const previewLineHistory = activeScenario?.previewMode
    ? (activeScenario.id === 'live' ? MOCK_LINE_HISTORY_LIVE : MOCK_LINE_HISTORY)
    : null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Game Detail Modal — Dev Preview</h1>
        <p className={styles.subtitle}>
          Mock fixtures below, or use real 2025 Week 8 data for a mid-season simulation (live API + game logs).
        </p>
      </header>

      <section className={styles.sectionBlock}>
        <h2 className={styles.sectionHeading}>2025 Real Data — Week 8 (mid-season)</h2>
        <p className={styles.sectionNote}>
          Uses your dev database and live matchup-stats API. Run{' '}
          <code>node scripts/seed2025MidSeasonPreview.js</code> in lotw-app-back once to load CORE ratings.
        </p>
        {real2025Loading && <p className={styles.statusMessage}>Loading 2025 Week 8 games…</p>}
        {!real2025Loading && real2025Error && (
          <p className={styles.statusError}>{real2025Error}</p>
        )}
        {!real2025Loading && !real2025Error && (
          <div className={styles.scenarioGrid}>
            {REAL_2025_PREVIEW_GAMES.map((config) => {
              const game = real2025Games.find((g) => g.id === config.gameId);
              const disabled = !game;
              return (
                <article key={config.id} className={styles.scenarioCard}>
                  <h3>{config.label}</h3>
                  <p>{config.description}</p>
                  {game ? (
                    <p className={styles.matchup}>
                      {game.away_team.rank && `#${game.away_team.rank} `}
                      {game.away_team.name}
                      {' @ '}
                      {game.home_team.name}
                      {game.home_team.rank && ` #${game.home_team.rank}`}
                    </p>
                  ) : (
                    <p className={styles.matchupMuted}>Game not in Week 8 payload</p>
                  )}
                  <button
                    type="button"
                    className={styles.openButton}
                    disabled={disabled}
                    onClick={() => openReal2025Scenario(config)}
                  >
                    Open modal
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.sectionBlock}>
        <h2 className={styles.sectionHeading}>Mock fixtures</h2>
        <div className={styles.scenarioGrid}>
          {GAME_DETAIL_PREVIEW_SCENARIOS.map((scenario) => (
            <article key={scenario.id} className={styles.scenarioCard}>
              <h3>{scenario.label}</h3>
              <p>{scenario.description}</p>
              <p className={styles.matchup}>
                #{scenario.game.away_team.rank} {scenario.game.away_team.name}
                {' @ '}
                {scenario.game.home_team.name} #{scenario.game.home_team.rank}
              </p>
              <button
                type="button"
                className={styles.openButton}
                onClick={() => handleOpen({ ...scenario, previewMode: true, displayedWeek })}
              >
                Open modal
              </button>
            </article>
          ))}
        </div>
      </section>

      {activeScenario && (
        <GameDetailModal
          game={activeScenario.game}
          displayedWeek={modalDisplayedWeek}
          isOpen
          onClose={handleClose}
          onPickAction={handlePickAction}
          initialSelectedPick={activeScenario.initialSelectedPick}
          previewLocks={activeScenario.previewLocks}
          previewMode={Boolean(activeScenario.previewMode)}
          previewMatchupStats={activeScenario.previewMode ? MOCK_MATCHUP_STATS : null}
          previewLineHistory={previewLineHistory}
          hasReachedMaxPicks={Boolean(activeScenario.previewLocks?.length)}
        />
      )}
    </div>
  );
};

export default GameDetailPreviewPage;
